"use client"

import { useState, useEffect } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { BookOpen, BookText, Code, Compass, FileText, GraduationCap, Laptop, LayoutGrid, Leaf, Lightbulb, PenTool, Presentation, Search, Sparkles, Bookmark, Clock, TrendingUp } from "lucide-react"

import { AnimatedBackground } from "@/components/animated-background"
import { ResourceCard } from "@/components/resource-card"
import { ResourceFilter } from "@/components/resource-filter"
import { ResourceHighlight } from "@/components/resource-highlight"
import { ResourceSection } from "@/components/resource-section"
import { ResourceTopicBadge } from "@/components/resource-topic-badge"
import { Button } from "@/components/ui/button"

// Sample resource data
const resourcesData = [
  {
    id: "1",
    title: "Mastering the Technical Interview: A Comprehensive Guide",
    description: "Learn proven strategies to ace technical interviews at top tech companies with this in-depth guide covering algorithms, system design, and behavioral questions.",
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=2070&fit=crop",
    type: "guide",
    url: "#",
    category: "Interview Prep",
    topics: ["Interview", "Career Development", "Technical Skills"],
    timeToConsume: "15 min read",
    isNew: true,
    isFeatured: true,
    date: "2025-04-01"
  },
  {
    id: "2",
    title: "How to Create an Effective Developer Portfolio",
    description: "Build a standout portfolio that showcases your skills and lands you job interviews. Learn what to include and how to present your work.",
    image: "https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?q=80&w=2055&fit=crop",
    type: "article",
    url: "#",
    category: "Portfolio Building",
    topics: ["Web Development", "Design", "Career Development"],
    timeToConsume: "8 min read",
    isNew: false,
    isFeatured: false,
    date: "2025-03-15"
  },
  {
    id: "3",
    title: "Introduction to System Design Principles",
    description: "A video walkthrough of fundamental system design concepts every developer should know, with real-world examples and case studies.",
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=2070&fit=crop",
    type: "video",
    url: "#",
    category: "System Design",
    topics: ["Architecture", "Scalability", "Technical Skills"],
    timeToConsume: "22 min watch",
    isNew: true,
    isFeatured: false,
    date: "2025-04-10"
  },
  {
    id: "4",
    title: "Resume Builder with AI-Powered Suggestions",
    description: "Create a professional resume tailored to your target role with AI-powered content suggestions and formatting tools.",
    image: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?q=80&w=2070&fit=crop",
    type: "tool",
    url: "#",
    category: "Resume Tools",
    topics: ["Resume", "AI Tools", "Career Development"],
    timeToConsume: "Free tool",
    isNew: false,
    isFeatured: false,
    date: "2025-02-28"
  },
  {
    id: "5",
    title: "Negotiating Your Tech Job Offer: A Practical Guide",
    description: "Learn how to confidently negotiate your salary and benefits package with practical scripts and strategies.",
    image: "https://images.unsplash.com/photo-1556155092-490a1ba16284?q=80&w=2070&fit=crop",
    type: "article",
    url: "#",
    category: "Salary Negotiation",
    topics: ["Negotiation", "Career Development", "Soft Skills"],
    timeToConsume: "12 min read",
    isNew: false,
    isFeatured: false,
    date: "2025-03-05"
  },
  {
    id: "6",
    title: "Frontend Development Roadmap 2025",
    description: "A comprehensive visual guide to becoming a frontend developer in 2025, covering essential skills, tools, and learning path.",
    image: "https://images.unsplash.com/photo-1593720213428-28a5b9e94613?q=80&w=2070&fit=crop",
    type: "guide",
    url: "#",
    category: "Learning Path",
    topics: ["Web Development", "Frontend", "Technical Skills"],
    timeToConsume: "10 min read",
    isNew: true,
    isFeatured: false,
    date: "2025-04-05"
  },
  {
    id: "7",
    title: "Coding Interview Question Solver",
    description: "Interactive tool to practice and solve common coding interview questions with step-by-step explanations and performance analysis.",
    image: "https://images.unsplash.com/photo-1580894732444-8ecded7900cd?q=80&w=2070&fit=crop",
    type: "tool",
    url: "#",
    category: "Interview Prep",
    topics: ["Interview", "Algorithms", "Technical Skills"],
    timeToConsume: "Interactive tool",
    isNew: false,
    isFeatured: false,
    date: "2025-01-20"
  },
  {
    id: "8",
    title: "Building a Personal Brand as a Developer",
    description: "Video course on how to build your personal brand as a developer through content creation, speaking, and community involvement.",
    image: "https://images.unsplash.com/photo-1493612276216-ee3925520721?q=80&w=2064&fit=crop",
    type: "video",
    url: "#",
    category: "Personal Branding",
    topics: ["Career Development", "Content Creation", "Soft Skills"],
    timeToConsume: "45 min watch",
    isNew: false,
    isFeatured: false,
    date: "2025-02-15"
  },
  {
    id: "9",
    title: "Cloud Architecture Best Practices",
    description: "Learn industry best practices for designing secure, scalable, and cost-effective cloud architectures on major platforms.",
    image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&fit=crop",
    type: "article",
    url: "#",
    category: "Cloud Computing",
    topics: ["Cloud", "Architecture", "Technical Skills"],
    timeToConsume: "18 min read",
    isNew: true,
    isFeatured: false,
    date: "2025-04-12"
  },
  {
    id: "10",
    title: "Soft Skills That Advance Your Tech Career",
    description: "Comprehensive guide to developing the soft skills that help technical professionals advance into leadership roles.",
    image: "https://images.unsplash.com/photo-1552581234-26160f608093?q=80&w=2070&fit=crop",
    type: "guide",
    url: "#",
    category: "Career Growth",
    topics: ["Leadership", "Communication", "Soft Skills"],
    timeToConsume: "14 min read",
    isNew: false,
    isFeatured: false,
    date: "2025-03-20"
  },
  {
    id: "11",
    title: "Salary Range Calculator for Tech Jobs",
    description: "Interactive tool to determine appropriate salary ranges based on your skills, experience, location, and current market trends.",
    image: "https://images.unsplash.com/photo-1554224155-8d04cb21ed6c?q=80&w=2022&fit=crop",
    type: "tool",
    url: "#",
    category: "Salary Tools",
    topics: ["Salary", "Career Development", "Market Research"],
    timeToConsume: "Free tool",
    isNew: false,
    isFeatured: false,
    date: "2025-02-10"
  },
  {
    id: "12",
    title: "How to Contribute to Open Source Projects",
    description: "Video walkthrough on finding and contributing to open source projects that match your skills and interests.",
    image: "https://images.unsplash.com/photo-1552664688-cf412ec27db2?q=80&w=2070&fit=crop",
    type: "video",
    url: "#",
    category: "Open Source",
    topics: ["Open Source", "Community", "Technical Skills"],
    timeToConsume: "28 min watch",
    isNew: false,
    isFeatured: false,
    date: "2025-03-10"
  }
];

// Extract unique topics from resource data
const allTopics = Array.from(
  new Set(
    resourcesData.flatMap(resource => resource.topics)
  )
).sort();

type Dictionary = Record<string, any>;

interface ResourcesClientProps {
  dictionary: Dictionary;
}

export function ResourcesClient({ dictionary }: ResourcesClientProps) {
  // State for filtering and searching
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [filteredResources, setFilteredResources] = useState(resourcesData);

  // Apply filters
  useEffect(() => {
    const filtered = resourcesData.filter(resource => {
      // Search query filter
      const matchesSearch = searchQuery === "" || 
        resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        resource.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        resource.category.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Type filter
      const matchesType = selectedType === "all" || resource.type === selectedType;
      
      // Topics filter
      const matchesTopics = selectedTopics.length === 0 || 
        selectedTopics.some(topic => resource.topics.includes(topic));
      
      return matchesSearch && matchesType && matchesTopics;
    });
    
    setFilteredResources(filtered);
  }, [searchQuery, selectedType, selectedTopics]);

  // Get featured resource
  const featuredResource = resourcesData.find(r => r.isFeatured);
  
  // Get new resources
  const newResources = resourcesData
    .filter(r => r.isNew && (!featuredResource || r.id !== featuredResource.id))
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.05 
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.3 }
    }
  };

  // Generate category icons
  const categoryIcons: {[key: string]: React.ReactNode} = {
    "Interview Prep": <Presentation className="h-5 w-5 text-indigo-500" />,
    "Portfolio Building": <FileText className="h-5 w-5 text-blue-500" />,
    "System Design": <Code className="h-5 w-5 text-violet-500" />,
    "Resume Tools": <FileText className="h-5 w-5 text-emerald-500" />,
    "Salary Negotiation": <Sparkles className="h-5 w-5 text-amber-500" />,
    "Learning Path": <Compass className="h-5 w-5 text-teal-500" />,
    "Personal Branding": <Leaf className="h-5 w-5 text-emerald-500" />,
    "Cloud Computing": <Laptop className="h-5 w-5 text-sky-500" />,
    "Career Growth": <GraduationCap className="h-5 w-5 text-orange-500" />,
    "Salary Tools": <Sparkles className="h-5 w-5 text-rose-500" />,
    "Open Source": <BookOpen className="h-5 w-5 text-green-500" />
  };

  return (
    <div className="relative min-h-screen pb-16">
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none select-none">
        <AnimatedBackground intensity={2} speed={3} />
        
        {/* Main gradient blob */}
        <div className="absolute top-0 left-0 right-0 w-[95%] h-96 bg-gradient-to-br from-emerald-300/20 via-teal-200/15 to-transparent dark:from-emerald-700/15 dark:via-teal-800/10 dark:to-transparent blur-[120px] transform -translate-y-1/4 rounded-full mx-auto"></div>
        
        {/* Floating decorative elements */}
        <div className="absolute top-[25%] right-[10%] w-32 h-32 rounded-full bg-gradient-to-br from-emerald-400/10 to-teal-300/5 dark:from-emerald-600/10 dark:to-teal-500/5 blur-xl animate-float-slow"></div>
        <div className="absolute top-[45%] left-[5%] w-40 h-40 rounded-full bg-gradient-to-br from-teal-400/10 to-emerald-300/5 dark:from-teal-600/10 dark:to-emerald-500/5 blur-xl animate-float-medium"></div>
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGZpbGw9IiMxMGIyODEiIGZpbGwtb3BhY2l0eT0iLjAyIiBkPSJNMzYgMzRoLTJ2LTJoMnYyem0tNCAwaDJ2LTJoMnptLTQgMGgydi0yaDB6Ii8+PC9nPjwvc3ZnPg==')] opacity-20 dark:opacity-10"></div>
      </div>
      
      {/* Header with enhanced animations */}
      <header className="container mx-auto py-12 md:py-16 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.2
                }
              }
            }}
          >
            <motion.h1 
              variants={{
                hidden: { opacity: 0, y: -20 },
                visible: { 
                  opacity: 1, 
                  y: 0,
                  transition: { duration: 0.6, type: "spring" }
                }
              }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4 bg-gradient-to-r from-emerald-600 to-teal-500 bg-clip-text text-transparent animate-gradient-x"
            >
              {dictionary.resources.title}
            </motion.h1>
            
            <motion.p 
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { 
                  opacity: 1, 
                  y: 0,
                  transition: { duration: 0.7 }
                }
              }}
              className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto"
            >
              {dictionary.resources.subtitle}
            </motion.p>
            
            {/* Decorative icons */}
            <div className="relative h-12 mt-8">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="flex justify-center items-center gap-8 text-muted-foreground/50"
              >
                <Bookmark className="h-6 w-6 animate-float-slow" />
                <FileText className="h-6 w-6 animate-float-medium" />
                <Presentation className="h-6 w-6 animate-float-fast" />
                <Code className="h-6 w-6 animate-float-medium" />
                <Laptop className="h-6 w-6 animate-float-slow" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </header>
      
      {/* Main content */}
      <main className="container mx-auto px-4 relative z-10 space-y-12">
        {/* Filters */}
        <ResourceFilter
          onSearch={setSearchQuery}
          onTypeFilter={setSelectedType}
          onTopicFilter={setSelectedTopics}
          selectedTopics={selectedTopics}
          dictionary={dictionary}
          topics={allTopics}
        />
        
        {/* Featured resource */}
        {featuredResource && !searchQuery && selectedType === 'all' && selectedTopics.length === 0 && (
          <section className="mb-12">
            <ResourceHighlight
              title={featuredResource.title}
              description={featuredResource.description}
              image={featuredResource.image}
              url={featuredResource.url}
              type={featuredResource.type}
              category={featuredResource.category}
              dictionary={dictionary}
            />
          </section>
        )}
        
        {/* New content section */}
        {newResources.length > 0 && !searchQuery && selectedType === 'all' && selectedTopics.length === 0 && (
          <ResourceSection
            title={dictionary.resources.newContent}
            icon={Sparkles}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {newResources.slice(0, 3).map((resource) => (
                <ResourceCard
                  key={resource.id}
                  id={resource.id}
                  title={resource.title}
                  description={resource.description}
                  image={resource.image}
                  type={resource.type as "article" | "video" | "tool" | "guide"}
                  url={resource.url}
                  category={resource.category}
                  timeToConsume={resource.timeToConsume}
                  isNew={resource.isNew}
                  dictionary={dictionary}
                />
              ))}
            </div>
          </ResourceSection>
        )}
        
        {/* Topics quick selection */}
        {!searchQuery && selectedType === 'all' && selectedTopics.length === 0 && (
          <ResourceSection
            title="Browse by Topic"
            icon={Bookmark}
            className="mb-10"
          >
            <div className="flex flex-wrap gap-2">
              {allTopics.map((topic) => (
                <ResourceTopicBadge
                  key={topic}
                  topic={topic}
                  isSelected={selectedTopics.includes(topic)}
                  onToggle={(topic: string) => {
                    if (selectedTopics.includes(topic)) {
                      setSelectedTopics(selectedTopics.filter(t => t !== topic));
                    } else {
                      setSelectedTopics([...selectedTopics, topic]);
                    }
                  }}
                />
              ))}
            </div>
          </ResourceSection>
        )}
        
        {/* Category quick links */}
        {!searchQuery && selectedType === 'all' && selectedTopics.length === 0 && (
          <ResourceSection
            title="Browse by Category"
            icon={LayoutGrid}
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
              {Array.from(new Set(resourcesData.map(r => r.category))).map((category) => (
                <motion.button
                  key={category}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                  onClick={() => setSearchQuery(category)}
                  className="p-4 rounded-lg border border-border/40 bg-card/50 hover:bg-card hover:shadow-md transition-all flex flex-col items-center justify-center gap-3 text-center"
                >
                  {categoryIcons[category] || <Lightbulb className="h-5 w-5 text-emerald-500" />}
                  <span className="text-sm font-medium">{category}</span>
                </motion.button>
              ))}
            </div>
          </ResourceSection>
        )}
        
        {/* All resources grid */}
        <ResourceSection
          title={searchQuery || selectedType !== 'all' || selectedTopics.length > 0
            ? `Search Results (${filteredResources.length})`
            : dictionary.resources.popular}
          icon={searchQuery || selectedType !== 'all' || selectedTopics.length > 0 ? Search : TrendingUp}
        >
          {(searchQuery || selectedType !== 'all' || selectedTopics.length > 0) && filteredResources.length === 0 ? (
            <div className="flex flex-col items-center mx-auto py-12">
              <Search className="h-16 w-16 text-muted-foreground/30 mb-4" />
              <h3 className="text-xl font-medium mb-2">No matching resources found</h3>
              <p className="text-muted-foreground mb-6 text-center">Try adjusting your search or filters</p>
              <Button 
                variant="default"
                onClick={() => {
                  setSearchQuery("");
                  setSelectedType("all");
                  setSelectedTopics([]);
                }}
              >
                Clear all filters
              </Button>
            </div>
          ) : (
            <motion.div 
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              <AnimatePresence>
                {filteredResources
                  .filter(r => !featuredResource || r.id !== featuredResource.id || searchQuery || selectedType !== 'all' || selectedTopics.length > 0)
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .map((resource, index) => (
                    <motion.div
                      key={resource.id}
                      variants={itemVariants}
                      layout
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.3 }}
                      custom={index}
                    >
                      <ResourceCard
                        id={resource.id}
                        title={resource.title}
                        description={resource.description}
                        image={resource.image}
                        type={resource.type as "article" | "video" | "tool" | "guide"}
                        url={resource.url}
                        category={resource.category}
                        timeToConsume={resource.timeToConsume}
                        isNew={resource.isNew}
                        dictionary={dictionary}
                      />
                    </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </ResourceSection>
      </main>
    </div>
  )
}
